import { fromEvent, Observable, Observer } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ImageSize } from './file.utils';

export class ImageUtils {
    /**
     * Convertit une URL d'image en URL base 64.
     */
    static getBase64ImageFromURL(url: string): Observable<string> {
        return new Observable(createObs);

        function createObs(observer: Observer<string>) {
            // create an image object
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.src = url;
            if (!img.complete) {
                // This will call another method that will create image from url
                img.onload = () => {
                    observer.next(ImageUtils.getBase64Image(img));
                    observer.complete();
                };
                img.onerror = (err) => {
                    observer.error(err);
                };
            } else {
                observer.next(ImageUtils.getBase64Image(img));
                observer.complete();
            }
        }
    }

    /**
     *  Crée une URL d'image en base 64 à partir d'une image HTML.
     */
    private static getBase64Image(img: HTMLImageElement): string {
        // We create a HTML canvas object that will create a 2d image
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx: CanvasRenderingContext2D = canvas.getContext('2d');
        // This will draw image
        ctx.drawImage(img, 0, 0);
        // Convert the drawn image to Data URL
        const dataURL: string = canvas.toDataURL('image/png');
        // this.base64DefaultURL = dataURL;
        return dataURL.replace(/^data:image\/(png|jpg);base64,/, '');
    }

    static getBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    }

    /**
     * Optimise une image avant upload :
     *  - redimensionne pour tenir dans maxWidth × maxHeight en conservant le ratio (jamais d'agrandissement),
     *  - convertit et compresse en WebP (repli JPEG si le navigateur ne sait pas exporter en WebP - Safari ne le supporte pas actuellement).
     *
     * La résolution par défaut (800 × 600) reste suffisante pour une impression correcte à 300 dpi.
     *
     * @param dataUrl image source au format Data URL (base64)
     * @param options maxWidth (800), maxHeight (600), quality (0.8 entre 0 et 1)
     * @returns la Data URL optimisée
     */
    static optimizeImageForUpload(
        dataUrl: string,
        { maxWidth = 800, maxHeight = 600, quality = 0.8 }: { maxWidth?: number; maxHeight?: number; quality?: number } = {},
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                // Calcul du facteur d'échelle pour tenir dans la boîte cible sans agrandir l'image d'origine.
                const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
                const targetWidth = Math.round(img.width * scale);
                const targetHeight = Math.round(img.height * scale);

                const canvas = document.createElement('canvas');
                canvas.width = targetWidth;
                canvas.height = targetHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    // Pas de contexte 2d : on renvoie l'image d'origine plutôt que d'échouer.
                    resolve(dataUrl);
                    return;
                }
                ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

                // Export WebP ; si non supporté, le résultat ne commence pas par
                // "data:image/webp" et on bascule sur du JPEG.
                let optimized = canvas.toDataURL('image/webp', quality);
                if (!optimized.startsWith('data:image/webp')) {
                    optimized = canvas.toDataURL('image/jpeg', quality);
                }
                resolve(optimized);
            };
            img.onerror = (err) => reject(err);
            img.src = dataUrl;
        });
    }

    /**
     * Renvoie la taille d'une image.
     */
    static getImgSize(imageSource: string): Observable<ImageSize> {
        const mapLoadedImage = (event): ImageSize => {
            return {
                width: event.target.width,
                height: event.target.height,
            };
        };
        const image = new Image();
        const $loadedImg = fromEvent(image, 'load').pipe(take(1), map(mapLoadedImage));
        image.src = imageSource;
        return $loadedImg;
    }
}
